import { writable, derived } from 'svelte/store';
import { api } from '../api/client';

// Person names another account, as kids mode lists parents and children.
export interface Person {
  user_id: string;
  display_name: string;
}

export interface User {
  id: string;
  username: string;
  display_name: string;
  admin: boolean;
  // can_create_groups follows the server's group creation setting (spec 001).
  can_create_groups?: boolean;
  time_zone?: string;
  timeline_enabled?: boolean;
  // phone and email are the member's optional contact details (spec 001 FR-007).
  phone?: string;
  email?: string;
  // photo is the version of the member's photo, and color theirs.
  photo?: string;
  color?: string;
  // weight_kg, birth_year, and sex estimate calories, and height_cm step length; only the member gets them (spec 006).
  weight_kg?: number;
  height_cm?: number;
  birth_year?: number;
  sex?: '' | 'female' | 'male';
  // max_hr is the member's own maximum heart rate for zones; absent for the estimate from age (spec 006).
  max_hr?: number;
  // auto_walks keeps the member's walks as activities on their own (spec 006 FR-106).
  auto_walks?: boolean;
  // managed_by names the parents of an account in kids mode, and
  // acting_parent the parent managing it in this tab (spec 001).
  managed_by?: Person[];
  acting_parent?: Person;
}

interface AuthState {
  user: User | null;
  loading: boolean;
  initialized: boolean;
}

function createAuthStore() {
  const { subscribe, set, update } = writable<AuthState>({
    user: null,
    loading: false,
    initialized: false,
  });

  return {
    subscribe,

    async init() {
      if (!api.getToken()) {
        set({ user: null, loading: false, initialized: true });
        return;
      }
      update((s) => ({ ...s, loading: true }));
      try {
        const user = await api.auth.me<User>();
        set({ user, loading: false, initialized: true });
      } catch {
        api.setToken(null);
        api.setRefreshToken(null);
        set({ user: null, loading: false, initialized: true });
      }
    },

    // setUser replaces the signed-in user after they change their profile.
    // A profile change answers with the account alone, so who manages it
    // carries over.
    setUser(user: User) {
      update((s) => ({
        ...s,
        user: { ...user, managed_by: user.managed_by ?? s.user?.managed_by, acting_parent: user.acting_parent ?? s.user?.acting_parent },
      }));
    },

    // adopt stores tokens issued outside login, such as by invite registration.
    adopt(accessToken: string, refreshToken: string, user: User) {
      api.setToken(accessToken);
      api.setRefreshToken(refreshToken);
      set({ user, loading: false, initialized: true });
    },

    async login(username: string, password: string) {
      update((s) => ({ ...s, loading: true }));
      try {
        const data = await api.auth.login<{
          access_token: string;
          refresh_token: string;
          user: User;
        }>(username, password);
        api.setToken(data.access_token);
        api.setRefreshToken(data.refresh_token);
        // The sign-in answer is the account alone. Who manages it comes with
        // /auth/me, and a child's settings must show locked from the first
        // page (spec 001 FR-098).
        const user = await api.auth.me<User>().catch(() => data.user);
        set({ user, loading: false, initialized: true });
        return user;
      } catch (err) {
        update((s) => ({ ...s, loading: false }));
        throw err;
      }
    },

    async logout() {
      try {
        await api.auth.logout();
      } catch {
        // Proceed even if the server-side logout fails.
      }
      api.setToken(null);
      api.setRefreshToken(null);
      set({ user: null, loading: false, initialized: true });
    },
  };
}

export const auth = createAuthStore();
export const isAuthenticated = derived(auth, ($a) => $a.user !== null);
export const currentUser = derived(auth, ($a) => $a.user);
// acting is a parent managing a child's settings in this tab (spec 001).
export const acting = derived(auth, ($a) => !!$a.user?.acting_parent);
// managedBy names the parents who manage the signed-in child, empty for a
// regular account or a parent acting, whose changes are allowed (001 FR-098).
export const managedBy = derived(auth, ($a) =>
  $a.user?.acting_parent ? '' : ($a.user?.managed_by ?? []).map((p) => p.display_name).join(', '),
);
